import { getMinPrice } from '@shared/lib/money';
import * as React from 'react';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import cn from 'classnames';

import {
	GdsService_OrderAdditionalServiceGdsServiceVip_Fragment as VipServiceFragment,
	GdsServiceVipSubType,
	OrderAdditionalServiceGdsServiceServiceType
} from '@websky/graphql';
import CheckoutAdditionalService from '../..';
import { useTheme } from '../../../theme';
import Modal from '../../../Modal';
import { getBusinessLounge, getSegments } from '../../../Checkout/store/order/selectors';
import { useIsMobile } from '../../../utils';
import { useOrderService } from '../hooks';
import { getCalculatedVipServicesPrice } from '../../../Checkout/store/orderPrice/selectors';
import { useRenderers } from '../../../renderProps';
import { ServiceButton } from '../../types';
import ServiceError from '../ServiceError/ServiceError';
import { useIsCanModifyServices } from '../../hooks';
import VipServiceSelect from '../../../VipServiceSelect';
import { closeRender } from '../Close/Close';
import { ServiceSummary } from '../../../features/service-summary';
import { CdnIconNames, SafeHtml } from '@shared/ui';
import { useGdsServiceEventToggleable } from '../../../Checkout/components/Checkout/hooks';

interface Props {
	services?: VipServiceFragment[];
	aggregationIcons?: React.ReactNode[] | CdnIconNames[];
	aggregationMode?: boolean;
	segmentId?: string;
}

const Business: React.FC<Props> = ({ services, aggregationIcons, aggregationMode, segmentId }) => {
	const { Business: theme } = useTheme('CheckoutAdditionalService');
	const { t } = useTranslation('Checkout');

	const segments = useSelector(getSegments);
	const businessServices = useSelector(getBusinessLounge);

	const businessLoungeServices = useMemo(() => {
		if (services?.length) {
			return services;
		}

		return businessServices;
	}, [services, businessServices]);

	const price = useSelector(getCalculatedVipServicesPrice(businessLoungeServices));

	const minPrice = getMinPrice(businessLoungeServices, service => service.price);

	const {
		isLoading,
		selectedServices,
		unconfirmedServices,
		renderPassenger,
		renderSubheader,
		onOpen,
		onClose,
		setOpened,
		onSaveHandler,
		onRemoveHandler,
		onRemoveCartService,
		passengers,
		opened
	} = useOrderService(businessLoungeServices, OrderAdditionalServiceGdsServiceServiceType.BusinessLounge);

	useGdsServiceEventToggleable({
		services: businessLoungeServices,
		serviceTypes: [
			OrderAdditionalServiceGdsServiceServiceType.BusinessLounge,
			OrderAdditionalServiceGdsServiceServiceType.Vip
		],
		serviceSubTypes: [GdsServiceVipSubType.BusinessLounge, GdsServiceVipSubType.BusinessLoungeInternational],
		onOpen
	});

	if (!businessLoungeServices.length) {
		return null;
	}

	const isMobile = useIsMobile();

	const isCanModifyServices = useIsCanModifyServices(businessLoungeServices);

	const title = useMemo(() => {
		if (services?.length === 1) {
			return services[0].name;
		}

		return t('Business lounge');
	}, [services]);

	const buttons: ServiceButton[] = useMemo(() => {
		if (unconfirmedServices.length) {
			return [
				{
					className: theme.button__remove,
					action: () => onRemoveHandler(unconfirmedServices),
					text: t('Remove'),
					buttonType: 'change',
					variant: 'optionEdit',
					isLoading
				}
			];
		}

		return [
			{
				action: () => setOpened(true),
				text: isCanModifyServices ? t('Change') : t('More details'),
				buttonType: 'change',
				isLoading
			}
		];
	}, [unconfirmedServices, isLoading]);

	const { CheckoutAdditionalService: CustomCheckoutAdditionalService } = useRenderers();
	const ServiceWrapper = CustomCheckoutAdditionalService?.CheckoutAdditionalService ?? CheckoutAdditionalService;

	return (
		<>
			<ServiceSummary
				serviceType="BusinessLounge"
				onDelete={onRemoveCartService}
				segmentId={segmentId}
			>
				{content => {
					const isSelected = !!content;

					return (
						<ServiceWrapper
							header={title}
							description={businessLoungeServices[0].description}
							className={theme.business}
							priceFrom={aggregationMode ? minPrice : isSelected ? price : null}
							priceTotal={price}
							aggregationIcons={aggregationIcons}
							mode={aggregationMode ? 'aggregator' : 'default'}
							isSelected={isSelected}
							coverClassName={theme.business_aggregator}
							addClick={onOpen}
							descriptionClassName={theme.descriptions}
							buttons={buttons}
							addButtonText={!isCanModifyServices ? t('More details') : null}
							isLoading={isLoading}
						>
							<ServiceError isShow={!!unconfirmedServices.length}>{content}</ServiceError>
						</ServiceWrapper>
					);
				}}
			</ServiceSummary>

			<Modal
				open={opened}
				classes={{
					scrollBody: theme.scrollBody,
					paper: theme.paper
				}}
				slideUp={isMobile}
				maxWidth="sm"
				closeButtonRenderer={closeRender}
				onClose={onClose}
			>
				<VipServiceSelect
					header={title}
					headerClassName={theme.header}
					className={cn(theme.serviceSelect, aggregationMode && theme.serviceSelect_aggregator)}
					subheader={
						<>
							{businessLoungeServices[0].vipFullDescription && (
								<SafeHtml tag="div" html={businessLoungeServices[0].vipFullDescription} />
							)}

							{renderSubheader()}
						</>
					}
					passengers={passengers}
					services={businessLoungeServices}
					segments={segments}
					selectedServices={selectedServices}
					type="checkbox"
					onSave={onSaveHandler}
					onClose={onClose}
					renderPassenger={renderPassenger}
					readOnly={!isCanModifyServices}
					showTotalPrice
					disableOnSameState
				/>
			</Modal>
		</>
	);
};

export default Business;
